import { usePanelBadges, type BadgeSection } from "@/lib/panel-badges";
import { SidebarBadge } from "@/components/layout/sidebar-badge";
import React, { ReactNode } from "react";
import { activeNavHref, type NavTarget } from "@/lib/active-nav";

const ACTIVE_TAB_CLASS = "bg-cyan-600 text-white hover:bg-cyan-600 hover:text-white font-semibold shadow-sm";
import { Link, useLocation } from "wouter";
import DesktopRecommendedBanner from "@/components/layout/DesktopRecommendedBanner";
import {
  BookOpen,
  Settings,
  Menu,
  X,
  ChevronRight,
  Home,
  GraduationCap,
  LogOut,
  ArrowRightLeft,
  Briefcase,
  Users,
  Award,
  ClipboardCheck, Banknote } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { useFirebaseAuth } from "@/hooks/use-firebase-auth";
import { usePanelAccess } from "@/hooks/use-panel-access";
import {
  canAccessProfessionalJobsProfile,
  canAccessSellerPanel,
  PROFESSIONAL_PANEL_PATH,
} from "@/lib/panel-switcher-access";
import Navbar from "./navbar";

interface InstructorPanelLayoutProps {
  children: ReactNode;
  activeTab?: string;
  setActiveTab?: (tabId: string) => void;
}

const instructorNavItems: (NavTarget & {
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  /** Sidebar count: assessments to mark, or what's new since last opened. */
  badge?: BadgeSection;
})[] = [
  { title: "Dashboard", href: "/instructor/dashboard", icon: Home, exactFor: ["/instructor"] },
  // The quiz builder is reached from a course (lesson -> Add Quiz, Quiz Management).
  { title: "My Courses", href: "/instructor/courses", icon: BookOpen, alsoFor: ["/instructor/exams"] },
  // The public class page (headline, bio). Courses are under My Courses, and
  // in Dashboard -> My Class; "My Class" here read as a second course list.
  { title: "Class page", href: "/instructor/class", icon: GraduationCap },
  { title: "Enrollments", href: "/instructor/enrollments", icon: Users, alsoFor: ["/instructor/students"], badge: "enrollments" },
  { title: "Earnings", href: "/instructor/earnings", icon: Banknote, alsoFor: ["/instructor/sales", "/instructor/payouts"], badge: "earnings" },
  { title: "Certificates", href: "/instructor/certificates", icon: Award, badge: "certificates" },
  { title: "Assessments", href: "/instructor/assessments", icon: ClipboardCheck, alsoFor: ["/instructor/essays"], badge: "assessments" },
  { title: "Settings", href: "/instructor/settings", icon: Settings },
];

export default function InstructorPanelLayout({ children }: InstructorPanelLayoutProps) {
  // Default closed on mobile so the sidebar doesn't cover the content.
  const [isSidebarOpen, setIsSidebarOpen] = React.useState(() => {
    if (typeof window === 'undefined') return true;
    return window.matchMedia('(min-width: 1024px)').matches;
  });
  const { toast } = useToast();
  const { logout: handleLogout, user } = useFirebaseAuth();
  const { isNobility, isRoyalty, isImperial, subscriptionTier, subscriptionStatus } = usePanelAccess();
  const [location, navigate] = useLocation();
  const { data: badges } = usePanelBadges('instructor');
  // The tab for this page, including its sub-pages (a course's edit page is
  // "My Courses"). Exact matching left most pages with no tab highlighted.
  const activeHref = activeNavHref(location, instructorNavItems);
  const sub = { isNobility, isRoyalty, isImperial, subscriptionTier, subscriptionStatus };
  const hasSellerAccess = canAccessSellerPanel(user, sub);
  const showProfessionalSwitcher = canAccessProfessionalJobsProfile(user, sub);

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const handleLogoutClick = async () => {
    try {
      await handleLogout();
      toast({
        title: "Logged out",
        description: "You have been successfully logged out.",
      });
    } catch (error) {
      toast({
        title: "Logout failed",
        description: "There was an error logging you out. Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="h-screen flex flex-col overflow-hidden bg-gradient-to-br from-gray-900 via-gray-900 to-gray-800 text-gray-100">
      <Navbar />
      {/* Navbar is `position: fixed` and no longer occupies flow height. This
          shell is a fixed h-screen box, so `margin-top` (not padding) is the
          correct compensation — margin is subtracted from a flex item's own
          share of the container's main-axis space, shrinking this row to fit
          within h-screen; padding would leave the row's total box at h-screen
          and just clip its own bottom content instead. */}
      <div className="flex flex-1 overflow-hidden mt-[var(--app-navbar-height,160px)]">
        <aside
          className={cn(
            "hidden lg:flex flex-col shrink-0 bg-gradient-to-b from-gray-900 to-gray-800 transition-all duration-300 ease-in-out z-50 border-r border-gray-700 h-full overflow-hidden",
            isSidebarOpen ? "w-64" : "w-16"
          )}
        >
          <div className="flex h-16 items-center justify-between border-b border-gray-700 px-4">
            {isSidebarOpen ? (
              <Link href="/instructor/dashboard">
                <button className="flex items-center gap-3 font-semibold hover:bg-gray-700/50 p-2 rounded-lg transition-colors">
                  <GraduationCap className="h-7 w-7 text-primary" />
                  <span className="text-sm">Instructor Panel</span>
                </button>
              </Link>
            ) : (
              <div className="w-7" />
            )}
            <Button variant="ghost" size="icon" onClick={toggleSidebar}>
              {isSidebarOpen ? <ChevronRight className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
            </Button>
          </div>

          <nav className="flex-1 grid gap-1 px-3 py-2 content-start overflow-y-auto">
            {instructorNavItems.map((item) => (
              <Link key={item.href} href={item.href}>
                               <Button
                  variant="ghost"
                  className={cn(
                    "w-full h-auto p-3 flex items-center gap-3",
                    isSidebarOpen ? "justify-start" : "justify-center px-2",
                    activeHref === item.href && ACTIVE_TAB_CLASS
                  )}
                  aria-current={activeHref === item.href ? "page" : undefined}
                >
                  <span className="relative shrink-0">
                    <item.icon className="h-5 w-5" />
                    {!isSidebarOpen && item.badge && <SidebarBadge count={badges?.[item.badge]} dot />}
                  </span>
                  {isSidebarOpen && (
                    <span className="text-sm font-medium truncate">{item.title}</span>
                  )}
                  {isSidebarOpen && item.badge && <SidebarBadge count={badges?.[item.badge]} label={item.title} />}
                </Button>
              </Link>
            ))}
          </nav>

          <div className="border-t border-gray-700 p-2">
            <div className="grid gap-1">
              {showProfessionalSwitcher && (
                <Button
                  variant="outline"
                  size="sm"
                  className={cn(
                    "w-full h-10 border-gray-600 text-gray-300 hover:bg-gray-700 hover:text-white",
                    isSidebarOpen ? "justify-start px-3" : "justify-center px-0"
                  )}
                  onClick={() => navigate(PROFESSIONAL_PANEL_PATH)}
                >
                  <Briefcase className="h-4 w-4" />
                  {isSidebarOpen && (
                    <span className="ml-2 truncate text-xs">Professional &amp; jobs</span>
                  )}
                </Button>
              )}
              {hasSellerAccess && (
                <Button
                  variant="outline"
                  size="sm"
                  className={cn(
                    "w-full h-10 border-gray-600 text-gray-300 hover:bg-gray-700 hover:text-white",
                    isSidebarOpen ? "justify-start px-3" : "justify-center px-0"
                  )}
                  onClick={() => navigate("/seller")}
                >
                  <ArrowRightLeft className="h-4 w-4" />
                  {isSidebarOpen && <span className="ml-2 truncate text-xs">Switch to Seller</span>}
                </Button>
              )}
              <Button
                variant="ghost"
                className={cn(
                  "w-full",
                  isSidebarOpen ? "justify-start" : "justify-center px-2"
                )}
                onClick={handleLogoutClick}
              >
                <LogOut className="h-4 w-4" />
                {isSidebarOpen && <span className="ml-2 text-xs">Sign Out</span>}
              </Button>
            </div>
            {isSidebarOpen && (
              <p className="text-xs text-gray-400 text-center mt-2">v{__APP_VERSION__}</p>
            )}
          </div>
        </aside>

        {isSidebarOpen && (
          <>
            <aside className="fixed inset-y-0 left-0 z-[60] flex flex-col bg-gradient-to-b from-gray-900 to-gray-800 w-64 lg:hidden border-r border-gray-700">
              <div className="flex h-16 items-center justify-between border-b border-gray-700 px-4">
                <button
                  type="button"
                  onClick={() => { window.location.href = "/instructor/dashboard"; }}
                  className="flex items-center gap-3 font-semibold hover:bg-gray-700/50 p-2 rounded-lg transition-colors"
                >
                  <GraduationCap className="h-7 w-7 text-primary" />
                  <span className="text-sm">Instructor Panel</span>
                </button>
                <Button variant="ghost" size="icon" onClick={toggleSidebar}>
                  <X className="h-4 w-4" />
                </Button>
              </div>

              <div className="flex-1 py-2 overflow-y-auto">
                <nav className="grid gap-1 px-3">
                  {instructorNavItems.map((item) => (
                    <Link key={`m-${item.href}`} href={item.href}>
                      <Button
                        variant="ghost"
                        className={cn("w-full h-auto p-3 justify-start", activeHref === item.href && ACTIVE_TAB_CLASS)}
                        aria-current={activeHref === item.href ? "page" : undefined}
                        onClick={() => setIsSidebarOpen(false)}
                      >
                        <item.icon className="h-5 w-5 mr-3 shrink-0" />
                        <span className="text-sm font-medium truncate">{item.title}</span>
                        {item.badge && <SidebarBadge count={badges?.[item.badge]} label={item.title} />}
                      </Button>
                    </Link>
                  ))}
                </nav>
              </div>

              <div className="border-t border-gray-700 p-2 mt-auto">
                <div className="grid gap-1">
                  {showProfessionalSwitcher && (
                    <Button
                      variant="outline"
                      size="sm"
                    className="h-11 w-full justify-start border-gray-600 px-3 text-gray-300 hover:bg-gray-700 hover:text-white"
                      onClick={() => {
                        setIsSidebarOpen(false);
                        navigate(PROFESSIONAL_PANEL_PATH);
                      }}
                    >
                      <Briefcase className="h-4 w-4" />
                      <span className="ml-2 truncate text-xs">Professional &amp; jobs</span>
                    </Button>
                  )}
                  {hasSellerAccess && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full h-10 justify-start px-3 border-gray-600 text-gray-300 hover:bg-gray-700 hover:text-white"
                      onClick={() => { setIsSidebarOpen(false); navigate("/seller"); }}
                    >
                      <ArrowRightLeft className="h-4 w-4" />
                      <span className="ml-2 truncate text-xs">Switch to Seller</span>
                    </Button>
                  )}
                  <Button variant="ghost" className="w-full justify-start" onClick={handleLogoutClick}>
                    <LogOut className="h-4 w-4" />
                    <span className="ml-2 text-xs">Sign Out</span>
                  </Button>
                </div>
                <p className="text-xs text-gray-400 text-center mt-2">v{__APP_VERSION__}</p>
              </div>
            </aside>
            <button
              type="button"
              className="fixed inset-0 z-40 bg-black/80 lg:hidden"
              onClick={toggleSidebar}
              aria-label="Close instructor panel navigation"
            />
          </>
        )}

        <div className="flex flex-1 flex-col overflow-hidden">
          <header className="sticky top-0 z-30 flex h-16 items-center gap-4 border-b border-gray-700 bg-gradient-to-r from-gray-900 to-gray-800 px-3 sm:px-4 lg:hidden">
            <Button variant="ghost" size="icon" className="h-11 w-11" onClick={toggleSidebar}>
              <Menu className="h-6 w-6" />
            </Button>
            <Link href="/instructor/dashboard">
              <Button variant="ghost" className="flex items-center gap-3 hover:bg-gray-700/50 p-2 rounded-lg transition-colors">
                <GraduationCap className="h-6 w-6 text-primary" />
                <span className="font-semibold text-sm">Instructor Panel</span>
              </Button>
            </Link>
          </header>

          <main className="flex-1 overflow-y-auto p-3 text-white sm:p-4 lg:p-6">
            <div className="max-w-7xl mx-auto">
              <DesktopRecommendedBanner panel="instructor" />
              {children}
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}
